export type Coords = {
  latitude: number;
  longitude: number;
  source?: "gps" | "network" | "ip";
  accuracyMeters?: number;
};

async function fetchJson(url: string, timeoutMs = 8000): Promise<unknown | null> {
  const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
  const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : null;
  try {
    const headers: Record<string, string> = { Accept: "application/json" };
    if (typeof document === "undefined") {
      headers["User-Agent"] = "Hermes/1.0 (https://rycso.es/hermes)";
    }
    const response = await fetch(url, {
      headers,
      signal: controller?.signal,
    });
    if (!response.ok) return null;
    return response.json();
  } catch {
    return null;
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/** Dirección escrita → coordenadas (Nominatim). */
export async function geocodeAddress(query: string): Promise<Coords | null> {
  const q = query.trim();
  if (q.length < 3) return null;
  const data = await fetchJson(
    `https://nominatim.openstreetmap.org/search?format=jsonv2&limit=1&q=${encodeURIComponent(q)}`,
  );
  const hit = Array.isArray(data) ? (data[0] as { lat?: string; lon?: string } | undefined) : null;
  const latitude = Number(hit?.lat);
  const longitude = Number(hit?.lon);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
  return { latitude, longitude, source: "network", accuracyMeters: 150 };
}

/** Último recurso (centro de ciudad aproximado). */
export async function fetchIpCoords(): Promise<Coords | null> {
  const geojs = (await fetchJson("https://get.geojs.io/v1/ip/geo.json")) as {
    latitude?: string;
    longitude?: string;
    city?: string;
  } | null;
  const lat = Number(geojs?.latitude);
  const lon = Number(geojs?.longitude);
  if (Number.isFinite(lat) && Number.isFinite(lon) && !(lat === 0 && lon === 0)) {
    return { latitude: lat, longitude: lon, source: "ip", accuracyMeters: 8000 };
  }
  const ipapi = (await fetchJson("https://ipapi.co/json/")) as {
    latitude?: number;
    longitude?: number;
  } | null;
  if (
    ipapi &&
    typeof ipapi.latitude === "number" &&
    typeof ipapi.longitude === "number" &&
    Number.isFinite(ipapi.latitude) &&
    Number.isFinite(ipapi.longitude)
  ) {
    return {
      latitude: ipapi.latitude,
      longitude: ipapi.longitude,
      source: "ip",
      accuracyMeters: 8000,
    };
  }
  return null;
}

type GpsOptions = {
  timeoutMs?: number;
  /** Preferir lecturas mejores que esto (m). */
  targetAccuracyM?: number;
  /** Soft: priorizar lecturas ≤ esto, pero no descartar peores. */
  maxAcceptableAccuracyM?: number;
  /** Si true, usa IP solo cuando el navegador no da ninguna posición. */
  allowIpFallback?: boolean;
};

const DEFAULT_TARGET_M = 80;
/** Escritorio/Wi‑Fi suele dar 200–2000 m; no descartar. */
const DEFAULT_MAX_ACCEPT_M = 5000;
/**
 * Solo descartar basura absurda (decenas de km).
 * Antes 2500 m rechazaba el GPS típico de Chrome en Wi‑Fi.
 */
const HARD_REJECT_M = 50000;

function validLatLon(lat: number, lon: number): boolean {
  return (
    Number.isFinite(lat) &&
    Number.isFinite(lon) &&
    Math.abs(lat) <= 90 &&
    Math.abs(lon) <= 180 &&
    !(lat === 0 && lon === 0)
  );
}

function oncePosition(
  highAccuracy: boolean,
  timeout: number,
  maximumAge: number,
): Promise<Coords | null> {
  return new Promise((resolve) => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      resolve(null);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude, accuracy } = pos.coords;
        if (!validLatLon(latitude, longitude)) {
          resolve(null);
          return;
        }
        const accuracyMeters = Number.isFinite(accuracy) ? accuracy : undefined;
        if (accuracyMeters != null && accuracyMeters > HARD_REJECT_M) {
          resolve(null);
          return;
        }
        resolve({
          latitude,
          longitude,
          source: highAccuracy ? "gps" : "network",
          accuracyMeters,
        });
      },
      () => resolve(null),
      {
        enableHighAccuracy: highAccuracy,
        timeout,
        maximumAge,
      },
    );
  });
}

/**
 * GPS del navegador. Acepta cualquier fix con lat/lon válidos.
 * Chrome en Wi‑Fi suele dar ±500–3000 m: eso es usable para planes cercanos.
 */
export async function getBrowserGps(opts: GpsOptions | number = {}): Promise<Coords | null> {
  const options: GpsOptions = typeof opts === "number" ? { timeoutMs: opts } : opts;
  const timeoutMs = options.timeoutMs ?? 16000;
  const targetAccuracyM = options.targetAccuracyM ?? DEFAULT_TARGET_M;
  const maxAcceptableAccuracyM = options.maxAcceptableAccuracyM ?? DEFAULT_MAX_ACCEPT_M;
  const allowIpFallback = options.allowIpFallback === true;

  if (typeof navigator === "undefined" || !navigator.geolocation) {
    return allowIpFallback ? fetchIpCoords() : null;
  }

  // 1) Intento rápido con caché reciente (máx 2 min).
  const cached = await oncePosition(false, Math.min(6000, timeoutMs), 120_000);
  if (cached && (cached.accuracyMeters == null || cached.accuracyMeters <= maxAcceptableAccuracyM)) {
    return cached;
  }

  // 2) Alta precisión + watch en paralelo.
  const watched = await new Promise<Coords | null>((resolve) => {
    let best: Coords | null = cached;
    let settled = false;
    let watchId: number | null = null;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let early: ReturnType<typeof setTimeout> | null = null;

    const finish = (value: Coords | null) => {
      if (settled) return;
      settled = true;
      if (watchId != null) {
        try {
          navigator.geolocation.clearWatch(watchId);
        } catch {
          /* ignore */
        }
      }
      if (timer) clearTimeout(timer);
      if (early) clearTimeout(early);
      resolve(value);
    };

    const consider = (pos: GeolocationPosition) => {
      const { latitude, longitude, accuracy } = pos.coords;
      if (!validLatLon(latitude, longitude)) return;
      const accuracyMeters = Number.isFinite(accuracy) ? accuracy : undefined;
      if (accuracyMeters != null && accuracyMeters > HARD_REJECT_M) return;
      const next: Coords = {
        latitude,
        longitude,
        source: "gps",
        accuracyMeters,
      };
      if (
        !best ||
        (next.accuracyMeters != null &&
          (best.accuracyMeters == null || next.accuracyMeters < best.accuracyMeters))
      ) {
        best = next;
      }
      if (next.accuracyMeters != null && next.accuracyMeters <= targetAccuracyM) {
        finish(next);
      }
    };

    timer = setTimeout(() => finish(best), timeoutMs);
    // Aceptar pronto si ya hay algo razonable (incluido Wi‑Fi ~1–2 km).
    early = setTimeout(() => {
      if (!settled && best) finish(best);
    }, 2800);

    navigator.geolocation.getCurrentPosition(consider, () => undefined, {
      enableHighAccuracy: true,
      timeout: Math.min(12000, timeoutMs),
      maximumAge: 0,
    });
    try {
      watchId = navigator.geolocation.watchPosition(consider, () => undefined, {
        enableHighAccuracy: true,
        timeout: timeoutMs,
        maximumAge: 0,
      });
    } catch {
      /* watch no disponible */
    }
  });

  if (watched) return watched;

  // 3) Red / Wi‑Fi sin alta precisión (a menudo el único que responde en PC).
  const network = await oncePosition(false, Math.min(10000, timeoutMs), 0);
  if (network) return network;

  // 4) Último recurso opcional: IP (marcado como ip).
  if (allowIpFallback) return fetchIpCoords();
  return null;
}

/** GPS para mapas/rutas. Sin IP por defecto. */
export async function resolveCoords(): Promise<Coords | null> {
  return getBrowserGps({
    timeoutMs: 18000,
    targetAccuracyM: 60,
    maxAcceptableAccuracyM: 5000,
    allowIpFallback: false,
  });
}

export type LiveGpsHandle = { stop: () => void };

function coordsFromPosition(pos: GeolocationPosition, source: Coords["source"]): Coords | null {
  const { latitude, longitude, accuracy } = pos.coords;
  if (!validLatLon(latitude, longitude)) return null;
  const accuracyMeters = Number.isFinite(accuracy) ? accuracy : undefined;
  if (accuracyMeters != null && accuracyMeters > HARD_REJECT_M) return null;
  return { latitude, longitude, source, accuracyMeters };
}

export function coordsMovedMeters(a: Coords, b: Coords): number {
  const R = 6371000;
  const dLat = ((b.latitude - a.latitude) * Math.PI) / 180;
  const dLon = ((b.longitude - a.longitude) * Math.PI) / 180;
  const lat1 = (a.latitude * Math.PI) / 180;
  const lat2 = (b.latitude * Math.PI) / 180;
  const x = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(x));
}

/**
 * GPS en vivo: no se corta a los 16 s. Si el usuario tarda en aceptar el permiso,
 * watchPosition sigue escuchando y emite en cuanto hay un fix.
 * Alta precisión a menudo no responde en PC/Wi‑Fi; se pide también red.
 */
export function watchLiveGps(
  onUpdate: (coords: Coords) => void,
  onError?: (message: string) => void,
): LiveGpsHandle {
  const watchIds: number[] = [];
  let stopped = false;
  let last: Coords | null = null;

  if (typeof navigator === "undefined" || !navigator.geolocation) {
    onError?.("Este navegador no ofrece geolocalización.");
    return { stop() {} };
  }

  const emit = (coords: Coords) => {
    if (stopped) return;
    if (
      last &&
      coordsMovedMeters(last, coords) < 10 &&
      (coords.accuracyMeters ?? 9999) >= (last.accuracyMeters ?? 9999) - 5
    ) {
      return;
    }
    last = coords;
    onUpdate(coords);
  };

  const onPos = (pos: GeolocationPosition, source: Coords["source"]) => {
    const coords = coordsFromPosition(pos, source);
    if (coords) emit(coords);
  };
  const onFail = (err: GeolocationPositionError) => {
    if (stopped) return;
    if (err.code === 1) onError?.("Permite la ubicación en el candado del navegador.");
  };

  navigator.geolocation.getCurrentPosition(
    (pos) => onPos(pos, "network"),
    onFail,
    { enableHighAccuracy: false, timeout: 12000, maximumAge: 60_000 },
  );
  navigator.geolocation.getCurrentPosition(
    (pos) => onPos(pos, "gps"),
    () => undefined,
    { enableHighAccuracy: true, timeout: 25000, maximumAge: 8000 },
  );

  try {
    watchIds.push(
      navigator.geolocation.watchPosition((pos) => onPos(pos, "network"), onFail, {
        enableHighAccuracy: false,
        timeout: 30000,
        maximumAge: 15000,
      }),
    );
    watchIds.push(
      navigator.geolocation.watchPosition((pos) => onPos(pos, "gps"), () => undefined, {
        enableHighAccuracy: true,
        timeout: 60000,
        maximumAge: 2000,
      }),
    );
  } catch {
    onError?.("No pude activar el GPS en vivo.");
  }

  return {
    stop() {
      stopped = true;
      for (const id of watchIds) {
        try {
          navigator.geolocation.clearWatch(id);
        } catch {
          /* ignore */
        }
      }
    },
  };
}
